import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Search, RefreshCw, CreditCard, Image, DollarSign,
  Copy, ExternalLink, FileText, Users, CheckCircle, XCircle, 
  AlertTriangle, Clock, Car, Shield, Activity, Eye, Lock, Unlock,
  Zap, Download, Play, TestTube, Server, Webhook, User, Calendar,
  Receipt, Camera, Ban, UserCheck, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

// Environment detection
const IS_PREVIEW = process.env.NODE_ENV !== 'production' || 
  window.location.hostname.includes('preview') ||
  window.location.hostname.includes('localhost');

const PlatformCommandCentre = () => {
  const { api, user } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Active section
  const [activeSection, setActiveSection] = useState('booking');
  
  // System Health State
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  
  // Booking Inspector State
  const [bookingId, setBookingId] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Payment Console State
  const [paymentBookingId, setPaymentBookingId] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Claims & Evidence State
  const [claimsBookingId, setClaimsBookingId] = useState('');
  const [claimsData, setClaimsData] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  
  // User Compliance State
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  
  // Webhook Events State
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  
  // Preview Test Tools State (only in preview)
  const [testResults, setTestResults] = useState({});
  const [testLoading, setTestLoading] = useState({});
  
  // Admin Tools State
  const [unlockBookingId, setUnlockBookingId] = useState('');
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  
  // Confirmation Dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    action: null,
    destructive: false
  });

  // Fetch system health on mount
  useEffect(() => {
    fetchSystemHealth();
  }, []);

  // ============================================
  // SYSTEM HEALTH
  // ============================================
  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const [envRes] = await Promise.all([
        api.get('/dev-debug/self-tests').catch(() => ({ data: {} })),
      ]);
      
      setSystemHealth({
        environment: IS_PREVIEW ? 'PREVIEW' : 'PRODUCTION',
        paymentProvider: 'airwallex',
        apiUrl: process.env.REACT_APP_BACKEND_URL,
        selfTests: envRes.data
      });
    } catch (error) {
      console.error('Error fetching system health:', error);
    } finally {
      setHealthLoading(false);
    }
  };

  // ============================================
  // BOOKING INSPECTOR
  // ============================================
  const fetchBookingDetails = async () => {
    if (!bookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setBookingLoading(true);
    try {
      // Fetch booking data with timeline
      const bookingRes = await api.get(`/bookings/${bookingId.trim()}`);
      const booking = bookingRes.data;
      
      // Fetch additional data
      const [evidenceRes, auditRes] = await Promise.all([
        api.get(`/bookings/${bookingId.trim()}/evidence/prehire`).catch(() => ({ data: {} })),
        api.get(`/platform-owner/audit-logs?entity_id=${bookingId.trim()}&limit=50`).catch(() => ({ data: { logs: [] } }))
      ]);
      
      // Build timeline from audit logs
      const timeline = (auditRes.data.logs || []).map(log => ({
        event: log.action,
        timestamp: log.timestamp,
        actor: log.user_email,
        details: log.details
      })).reverse();
      
      setBookingData({
        ...booking,
        timeline,
        evidence: evidenceRes.data,
        fetched_at: new Date().toISOString()
      });
      
      toast.success('Booking data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch booking');
      setBookingData(null);
    } finally {
      setBookingLoading(false);
    }
  };

  // ============================================
  // PAYMENT CONSOLE
  // ============================================
  const fetchPaymentDetails = async () => {
    if (!paymentBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setPaymentLoading(true);
    try {
      const bookingRes = await api.get(`/bookings/${paymentBookingId.trim()}`);
      const booking = bookingRes.data;
      
      // Build payment timeline from booking data
      const paymentTimeline = [];
      
      if (booking.checkout_session_id) {
        paymentTimeline.push({
          event: 'checkout_session_created',
          timestamp: booking.created_at,
          stripe_id: booking.checkout_session_id
        });
      }
      
      if (booking.stripe_payment_intent_id) {
        paymentTimeline.push({
          event: 'payment_intent_created',
          timestamp: booking.payment_captured_at || booking.updated_at,
          stripe_id: booking.stripe_payment_intent_id
        });
      }
      
      if (booking.payment_status === 'captured') {
        paymentTimeline.push({
          event: 'payment_captured',
          timestamp: booking.payment_captured_at,
          amount: booking.total_amount
        });
      }
      
      if (booking.bond_payment_intent_id) {
        paymentTimeline.push({
          event: `bond_${booking.bond_status}`,
          timestamp: booking.bond_authorized_at || booking.updated_at,
          stripe_id: booking.bond_payment_intent_id,
          amount: booking.bond_amount
        });
      }
      
      if (booking.payout_transfer_id) {
        paymentTimeline.push({
          event: 'payout_transferred',
          timestamp: booking.payout_at,
          stripe_id: booking.payout_transfer_id,
          amount: booking.payout_amount_cents / 100
        });
      }
      
      setPaymentData({
        booking_id: booking.booking_id,
        payment_status: booking.payment_status,
        bond_status: booking.bond_status,
        payout_status: booking.payout_status,
        
        // Amounts
        subtotal: booking.subtotal,
        platform_fee: booking.platform_fee_amount_cents / 100,
        total_amount: booking.total_amount,
        bond_amount: booking.bond_amount,
        
        // Payment IDs
        checkout_session_id: booking.checkout_session_id,
        payment_intent_id: booking.stripe_payment_intent_id,
        bond_payment_intent_id: booking.bond_payment_intent_id,
        payout_transfer_id: booking.payout_transfer_id,
        
        // Ledger
        payment_ledger: booking.payment_ledger,
        
        // Timeline
        timeline: paymentTimeline,
        
        fetched_at: new Date().toISOString()
      });
      
      toast.success('Payment data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch payment details');
      setPaymentData(null);
    } finally {
      setPaymentLoading(false);
    }
  };

  // ============================================
  // CLAIMS & EVIDENCE
  // ============================================
  const fetchClaimsEvidence = async () => {
    if (!claimsBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setClaimsLoading(true);
    try {
      const [prehireRes, posthireRes, claimsRes] = await Promise.all([
        api.get(`/bookings/${claimsBookingId.trim()}/evidence/prehire`).catch(() => ({ data: {} })),
        api.get(`/bookings/${claimsBookingId.trim()}/evidence/posthire`).catch(() => ({ data: {} })),
        api.get(`/host/claims?booking_id=${claimsBookingId.trim()}`).catch(() => ({ data: { claims: [] } }))
      ]);
      
      setClaimsData({
        booking_id: claimsBookingId.trim(),
        prehire: prehireRes.data,
        posthire: posthireRes.data,
        claims: claimsRes.data.claims || [],
        fetched_at: new Date().toISOString()
      });
      
      toast.success('Evidence data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch evidence');
      setClaimsData(null);
    } finally {
      setClaimsLoading(false);
    }
  };

  // ============================================
  // USER COMPLIANCE
  // ============================================
  const fetchUserCompliance = async () => {
    if (!userSearchQuery.trim()) {
      toast.error('Please enter a user ID or email');
      return;
    }
    
    setUserLoading(true);
    try {
      // Try to find user by email or ID
      const searchRes = await api.get(`/admin/users?search=${encodeURIComponent(userSearchQuery.trim())}`);
      const users = searchRes.data.users || [];
      
      if (users.length === 0) {
        toast.error('No user found');
        setUserData(null);
        return;
      }
      
      const user = users[0];
      
      // Fetch additional compliance data
      const [verificationRes, auditRes] = await Promise.all([
        api.get(`/admin/verifications?user_id=${user.user_id}`).catch(() => ({ data: { verifications: [] } })),
        api.get(`/platform-owner/audit-logs?user_id=${user.user_id}&limit=20`).catch(() => ({ data: { logs: [] } }))
      ]);
      
      setUserData({
        ...user,
        verifications: verificationRes.data.verifications || [],
        audit_logs: auditRes.data.logs || [],
        fetched_at: new Date().toISOString()
      });
      
      toast.success('User compliance data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch user data');
      setUserData(null);
    } finally {
      setUserLoading(false);
    }
  };

  // ============================================
  // WEBHOOK EVENTS
  // ============================================
  const fetchWebhookEvents = async () => {
    setWebhookLoading(true);
    try {
      const eventsRes = await api.get('/dev-debug/event-log');
      setWebhookEvents(eventsRes.data.events || []);
      toast.success('Webhook events refreshed');
    } catch (error) {
      toast.error('Failed to fetch webhook events');
    } finally {
      setWebhookLoading(false);
    }
  };

  // ============================================
  // ADMIN TOOLS
  // ============================================
  const handleUnlockReturn = async () => {
    if (!unlockBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setAdminActionLoading(true);
    try {
      await api.post(`/admin/bookings/${unlockBookingId.trim()}/unlock_return`, {
        reason: 'Manual unlock via Platform Command Centre'
      });
      toast.success('Return checklist unlocked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock return');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleLockReturn = async () => {
    if (!unlockBookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    
    setAdminActionLoading(true);
    try {
      await api.post(`/admin/bookings/${unlockBookingId.trim()}/lock_return`);
      toast.success('Return checklist locked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock return');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      const res = await api.get('/dev-debug/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `platform-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  // ============================================
  // PREVIEW TEST TOOLS (Only in Preview)
  // ============================================
  const runPreviewTest = async (testName, endpoint) => {
    if (!IS_PREVIEW) {
      toast.error('Test tools only available in preview environment');
      return;
    }
    
    setTestLoading(prev => ({ ...prev, [testName]: true }));
    try {
      const res = await api.post(endpoint);
      setTestResults(prev => ({ ...prev, [testName]: res.data }));
      
      if (res.data.summary?.all_passed) {
        toast.success(`${testName} PASSED`);
      } else {
        toast.error(`${testName} had failures`);
      }
    } catch (error) {
      toast.error(`Failed to run ${testName}`);
      setTestResults(prev => ({ ...prev, [testName]: { error: error.message } }));
    } finally {
      setTestLoading(prev => ({ ...prev, [testName]: false }));
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Format date helper
  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  // Format currency helper
  const formatAUD = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  // Section navigation items
  const sections = [
    { id: 'booking', icon: Search, label: 'Booking Inspector' },
    { id: 'payment', icon: CreditCard, label: 'Payment Console' },
    { id: 'claims', icon: Camera, label: 'Claims & Evidence' },
    { id: 'user', icon: UserCheck, label: 'User Compliance' },
    { id: 'health', icon: Activity, label: 'System Health' },
    { id: 'webhooks', icon: Webhook, label: 'Webhook Events' },
    ...(IS_PREVIEW ? [{ id: 'tests', icon: TestTube, label: 'Preview Tests' }] : [])
  ];

  // Header badges for the shell
  const headerBadges = (
    <div className="flex items-center gap-2">
      <Badge className={IS_PREVIEW 
        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
        : 'bg-red-500/20 text-red-400 border-red-500/50'
      }>
        {IS_PREVIEW ? 'PREVIEW' : 'PRODUCTION'}
      </Badge>
    </div>
  );

  return (
    <PlatformOwnerAppShell 
      title="Platform Command Centre" 
      activeId="debug"
      headerBadge={headerBadges}
    >
      {/* Environment Banner */}
      <div className={`w-full py-2 text-center text-sm font-semibold rounded-lg mb-4 ${isHostDirect ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'}`}>
        {isHostDirect
          ? 'DIRECT PAYMENT MODE — HireHub is not processing payments or bonds'
          : 'AIRWALLEX DEMO — Payment Provider Active'}
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map(section => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? 'default' : 'outline'}
            className={activeSection === section.id 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'border-slate-600 text-slate-300 hover:bg-slate-700'
            }
            onClick={() => setActiveSection(section.id)}
          >
            <section.icon className="w-4 h-4 mr-2" />
            {section.label}
          </Button>
        ))}
      </div>

        {/* Section Content */}
        <div className="space-y-6">
          
          {/* ============================================ */}
          {/* SECTION 1: BOOKING INSPECTOR */}
          {/* ============================================ */}
          {activeSection === 'booking' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-blue-400" />
                    Live Booking Inspector
                  </CardTitle>
                  <CardDescription>
                    Investigate any booking's complete lifecycle
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter Booking ID (e.g., book_xxxxx)"
                      value={bookingId}
                      onChange={(e) => setBookingId(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && fetchBookingDetails()}
                    />
                    <Button 
                      onClick={fetchBookingDetails}
                      disabled={bookingLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {bookingLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Inspect
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {bookingData && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Booking: {bookingData.booking_id}</span>
                      <Badge className={
                        bookingData.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        bookingData.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        bookingData.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }>
                        {bookingData.status?.toUpperCase()}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Quick Links */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Button variant="outline" size="sm" className="border-slate-600" onClick={() => copyToClipboard(bookingData.customer_id)}>
                        <User className="w-4 h-4 mr-2" /> Renter: {bookingData.customer_id?.slice(0, 12)}...
                      </Button>
                      <Button variant="outline" size="sm" className="border-slate-600" onClick={() => copyToClipboard(bookingData.owner_id)}>
                        <User className="w-4 h-4 mr-2" /> Host: {bookingData.owner_id?.slice(0, 12)}...
                      </Button>
                      <Button variant="outline" size="sm" className="border-slate-600" onClick={() => copyToClipboard(bookingData.vehicle_id)}>
                        <Car className="w-4 h-4 mr-2" /> Vehicle: {bookingData.vehicle_id?.slice(0, 12)}...
                      </Button>
                      <Button variant="outline" size="sm" className="border-slate-600" onClick={() => { setPaymentBookingId(bookingData.booking_id); setActiveSection('payment'); }}>
                        <CreditCard className="w-4 h-4 mr-2" /> View Payment
                      </Button>
                    </div>

                    {/* Booking Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Dates</p>
                        <p className="text-sm font-medium">{bookingData.start_date} → {bookingData.end_date}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Total Amount</p>
                        <p className="text-sm font-medium">{formatAUD(bookingData.total_amount)}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Bond Status</p>
                        <p className="text-sm font-medium">{bookingData.bond_status || 'N/A'}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Payout Status</p>
                        <p className="text-sm font-medium">{bookingData.payout_status || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Lifecycle Timeline */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-400" />
                        Lifecycle Timeline
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {bookingData.timeline?.length > 0 ? bookingData.timeline.map((event, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm bg-slate-700/30 p-2 rounded">
                            <div className="w-2 h-2 mt-2 rounded-full bg-blue-400" />
                            <div className="flex-1">
                              <p className="font-medium">{event.event}</p>
                              <p className="text-xs text-slate-400">{formatDate(event.timestamp)} • {event.actor || 'System'}</p>
                            </div>
                          </div>
                        )) : (
                          <p className="text-sm text-slate-400">No timeline events found</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 2: PAYMENT CONSOLE */}
          {/* ============================================ */}
          {activeSection === 'payment' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-400" />
                    Payment Console
                  </CardTitle>
                  <CardDescription>
                    View payment intents, bonds, and payout status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter Booking ID"
                      value={paymentBookingId}
                      onChange={(e) => setPaymentBookingId(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && fetchPaymentDetails()}
                    />
                    <Button 
                      onClick={fetchPaymentDetails}
                      disabled={paymentLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {paymentLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Load
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {paymentData && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle>Payment Details: {paymentData.booking_id}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Status Badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge className={paymentData.payment_status === 'captured' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        Payment: {paymentData.payment_status}
                      </Badge>
                      <Badge className={paymentData.bond_status === 'released' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}>
                        Bond: {paymentData.bond_status || 'N/A'}
                      </Badge>
                      <Badge className={paymentData.payout_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                        Payout: {paymentData.payout_status || 'N/A'}
                      </Badge>
                    </div>

                    {/* Amount Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Total Paid</p>
                        <p className="text-lg font-semibold">{formatAUD(paymentData.total_amount)}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Platform Fee</p>
                        {isHostDirect ? (
                          <p className="text-sm italic text-slate-400" data-testid="cc-platform-fee-hostdirect">
                            No platform fee (direct payment mode)
                          </p>
                        ) : (
                          <p className="text-lg font-semibold">{formatAUD(paymentData.platform_fee)}</p>
                        )}
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Bond Amount</p>
                        <p className="text-lg font-semibold">{formatAUD(paymentData.bond_amount)}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Host Payout</p>
                        <p className="text-lg font-semibold">{formatAUD(paymentData.payment_ledger?.host_payout_cents / 100)}</p>
                      </div>
                    </div>

                    {/* Payment IDs */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        Payment References
                        <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                          {paymentData.payment_provider || 'airwallex'}
                        </Badge>
                      </h4>
                      <div className="space-y-2">
                        {paymentData.checkout_session_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Checkout Session</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.checkout_session_id}</span>
                          </div>
                        )}
                        {paymentData.payment_intent_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Payment Intent</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.payment_intent_id}</span>
                          </div>
                        )}
                        {paymentData.bond_payment_intent_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Bond Intent</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.bond_payment_intent_id}</span>
                          </div>
                        )}
                        {paymentData.payout_transfer_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Payout Transfer</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.payout_transfer_id}</span>
                          </div>
                        )}
                        {paymentData.stripe_charge_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Charge ID</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.stripe_charge_id}</span>
                          </div>
                        )}
                        {paymentData.refund_id && (
                          <div className="flex justify-between text-xs bg-slate-700/50 p-2 rounded">
                            <span className="text-slate-400">Refund ID</span>
                            <span className="font-mono text-slate-300 truncate ml-2 max-w-[200px]">{paymentData.refund_id}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Timeline */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3">Payment Timeline</h4>
                      <div className="space-y-2">
                        {paymentData.timeline?.map((event, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm bg-slate-700/30 p-2 rounded">
                            <div className={`w-2 h-2 rounded-full ${
                              event.event.includes('succeeded') || event.event.includes('captured') ? 'bg-green-400' :
                              event.event.includes('failed') ? 'bg-red-400' : 'bg-blue-400'
                            }`} />
                            <span className="flex-1">{event.event}</span>
                            {event.amount && <span className="text-slate-400">{formatAUD(event.amount)}</span>}
                            <span className="text-xs text-slate-400">{formatDate(event.timestamp)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 3: CLAIMS & EVIDENCE */}
          {/* ============================================ */}
          {activeSection === 'claims' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-orange-400" />
                    Claims & Evidence Centre
                  </CardTitle>
                  <CardDescription>
                    View pre-hire, post-hire photos, and claim evidence
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter Booking ID"
                      value={claimsBookingId}
                      onChange={(e) => setClaimsBookingId(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && fetchClaimsEvidence()}
                    />
                    <Button 
                      onClick={fetchClaimsEvidence}
                      disabled={claimsLoading}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {claimsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      Load Evidence
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {claimsData && (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pre-Hire Evidence */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-base">Pre-Hire Checklist</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {claimsData.prehire?.exterior_photos?.length > 0 ? (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-400">Exterior: {claimsData.prehire.exterior_photos?.length || 0} photos</p>
                          <p className="text-sm text-slate-400">Interior: {claimsData.prehire.interior_photos?.length || 0} photos</p>
                          <p className="text-sm text-slate-400">Odometer: {claimsData.prehire.odometer_reading || 'N/A'} km</p>
                          <p className="text-sm text-slate-400">Fuel Level: {claimsData.prehire.fuel_level || 'N/A'}</p>
                          <p className="text-sm text-slate-400">Submitted: {formatDate(claimsData.prehire.submitted_at)}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No pre-hire evidence found</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Post-Hire Evidence */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-base">Post-Hire Checklist</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {claimsData.posthire?.exterior_photos?.length > 0 ? (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-400">Exterior: {claimsData.posthire.exterior_photos?.length || 0} photos</p>
                          <p className="text-sm text-slate-400">Interior: {claimsData.posthire.interior_photos?.length || 0} photos</p>
                          <p className="text-sm text-slate-400">Return Odometer: {claimsData.posthire.return_odometer || 'N/A'} km</p>
                          <p className="text-sm text-slate-400">Return Fuel: {claimsData.posthire.return_fuel_level || 'N/A'}</p>
                          <p className="text-sm text-slate-400">Submitted: {formatDate(claimsData.posthire.submitted_at)}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No post-hire evidence found</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Claims */}
                  <Card className="bg-slate-800 border-slate-700 md:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-base">Claims ({claimsData.claims?.length || 0})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {claimsData.claims?.length > 0 ? (
                        <div className="space-y-3">
                          {claimsData.claims.map((claim, i) => (
                            <div key={i} className="flex items-center justify-between bg-slate-700/30 p-3 rounded">
                              <div>
                                <p className="font-medium">{claim.claim_type}</p>
                                <p className="text-xs text-slate-400">{claim.description?.slice(0, 50)}...</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">{formatAUD(claim.amount_cents / 100)}</p>
                                <Badge className={
                                  claim.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                  claim.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }>{claim.status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No claims for this booking</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 4: USER COMPLIANCE */}
          {/* ============================================ */}
          {activeSection === 'user' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-green-400" />
                    User Compliance Centre
                  </CardTitle>
                  <CardDescription>
                    View verification status, legal acceptance, and account flags
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter User ID or Email"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && fetchUserCompliance()}
                    />
                    <Button 
                      onClick={fetchUserCompliance}
                      disabled={userLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {userLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Search
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {userData && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{userData.full_name || userData.email}</span>
                      <div className="flex gap-2">
                        <Badge className={userData.verification_status === 'verified' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {userData.verification_status || 'unverified'}
                        </Badge>
                        {userData.host_status && (
                          <Badge className="bg-blue-500/20 text-blue-400">Host: {userData.host_status}</Badge>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* User Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">User ID</p>
                        <p className="text-sm font-mono truncate">{userData.user_id}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Email</p>
                        <p className="text-sm truncate">{userData.email}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Created</p>
                        <p className="text-sm">{formatDate(userData.created_at)}</p>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded">
                        <p className="text-xs text-slate-400">Last Login</p>
                        <p className="text-sm">{formatDate(userData.last_login_at)}</p>
                      </div>
                    </div>

                    {/* Legal Acceptance */}
                    <div className="bg-slate-700/50 p-4 rounded">
                      <h4 className="text-sm font-semibold mb-2">Legal Acceptance</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-400">Terms Version:</span>
                          <span className="ml-2">{userData.terms_version_accepted || 'Not accepted'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Privacy Version:</span>
                          <span className="ml-2">{userData.privacy_version_accepted || 'Not accepted'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Host Agreement:</span>
                          <span className="ml-2">{userData.host_agreement_s3_key ? 'Signed' : 'Not signed'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Accepted At:</span>
                          <span className="ml-2">{formatDate(userData.terms_accepted_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Recent Audit Logs */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3">Recent Activity</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userData.audit_logs?.slice(0, 10).map((log, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm bg-slate-700/30 p-2 rounded">
                            <span className="flex-1">{log.action}</span>
                            <span className="text-xs text-slate-400">{formatDate(log.timestamp)}</span>
                          </div>
                        ))}
                        {!userData.audit_logs?.length && (
                          <p className="text-sm text-slate-400">No recent activity</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 5: SYSTEM HEALTH */}
          {/* ============================================ */}
          {activeSection === 'health' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    System Health
                  </CardTitle>
                  <CardDescription>
                    Environment status and configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-700/50 p-4 rounded">
                      <p className="text-xs text-slate-400">Environment</p>
                      <p className={`text-lg font-semibold ${IS_PREVIEW ? 'text-yellow-400' : 'text-red-400'}`}>
                        {systemHealth?.environment || 'Loading...'}
                      </p>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded">
                      <p className="text-xs text-slate-400">Payment Provider</p>
                      <p className="text-lg font-semibold text-blue-400">
                        {systemHealth?.paymentProvider || 'airwallex'}
                      </p>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded">
                      <p className="text-xs text-slate-400">Provider Active</p>
                      <p className="text-lg font-semibold">
                        <CheckCircle className="w-5 h-5 text-green-400 inline" />
                      </p>
                    </div>
                    <div className="bg-slate-700/50 p-4 rounded">
                      <p className="text-xs text-slate-400">API URL</p>
                      <p className="text-xs font-mono truncate">{systemHealth?.apiUrl || 'Loading...'}</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={fetchSystemHealth} disabled={healthLoading} variant="outline" className="border-slate-600">
                      {healthLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Refresh Health
                    </Button>
                    <Button onClick={exportLogs} variant="outline" className="border-slate-600">
                      <Download className="w-4 h-4 mr-2" />
                      Export Logs
                    </Button>
                  </div>

                  {/* Admin Tools */}
                  <div className="border-t border-slate-700 pt-6">
                    <h4 className="text-sm font-semibold mb-4">Admin Tools</h4>
                    <div className="bg-slate-700/30 p-4 rounded space-y-3">
                      <p className="text-sm text-slate-400">Unlock/Lock Return Checklist</p>
                      <div className="flex gap-3">
                        <Input
                          placeholder="Booking ID"
                          value={unlockBookingId}
                          onChange={(e) => setUnlockBookingId(e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white max-w-xs"
                        />
                        <Button 
                          onClick={handleUnlockReturn}
                          disabled={adminActionLoading}
                          variant="outline"
                          className="border-green-600 text-green-400"
                        >
                          <Unlock className="w-4 h-4 mr-2" />
                          Unlock
                        </Button>
                        <Button 
                          onClick={handleLockReturn}
                          disabled={adminActionLoading}
                          variant="outline"
                          className="border-red-600 text-red-400"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Lock
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 6: WEBHOOK EVENTS */}
          {/* ============================================ */}
          {activeSection === 'webhooks' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Webhook className="w-5 h-5 text-indigo-400" />
                    Webhook Event Monitor
                  </CardTitle>
                  <CardDescription>
                    View recent payment and system webhook events
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={fetchWebhookEvents} disabled={webhookLoading} className="bg-indigo-600 hover:bg-indigo-700">
                    {webhookLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Refresh Events
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-base">Recent Events ({webhookEvents.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {webhookEvents.length > 0 ? webhookEvents.map((event, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm bg-slate-700/30 p-3 rounded">
                        <div className={`w-2 h-2 rounded-full ${
                          event.type?.includes('succeeded') ? 'bg-green-400' :
                          event.type?.includes('failed') ? 'bg-red-400' : 'bg-blue-400'
                        }`} />
                        <div className="flex-1">
                          <p className="font-medium">{event.type || event.event}</p>
                          <p className="text-xs text-slate-400">{event.entity_type} • {event.entity_id?.slice(0, 20)}...</p>
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(event.timestamp)}</span>
                      </div>
                    )) : (
                      <p className="text-sm text-slate-400">No webhook events found. Click refresh to load.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 7: PREVIEW TEST TOOLS (Preview Only) */}
          {/* ============================================ */}
          {activeSection === 'tests' && IS_PREVIEW && (
            <div className="space-y-6">
              <Card className="bg-yellow-900/30 border-yellow-600/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-yellow-400">
                    <AlertTriangle className="w-5 h-5" />
                    Preview Test Tools
                  </CardTitle>
                  <CardDescription className="text-yellow-300/70">
                    These tools are only available in preview environment. They create test data and should never be used in production.
                  </CardDescription>
                </CardHeader>
              </Card>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'E2E Lifecycle', endpoint: '/dev-debug/test-e2e-full-lifecycle', icon: Zap },
                  { name: 'Overdue Protection', endpoint: '/dev-debug/test-overdue-protection', icon: Clock },
                  { name: 'Legal Re-Acceptance', endpoint: '/dev-debug/test-legal-re-acceptance', icon: Shield },
                  { name: 'Extension Flow', endpoint: '/dev-debug/test-extension-flow', icon: Calendar },
                  { name: 'Messaging Smoke', endpoint: '/dev-debug/test-messaging-smoke', icon: FileText },
                  { name: 'Rego Enforcement', endpoint: '/dev-debug/test-rego-enforcement', icon: Car },
                  { name: 'Cancellation Math', endpoint: '/dev-debug/test-cancellation-math', icon: Receipt },
                  { name: 'Dispute Workflow', endpoint: '/dev-debug/test-dispute-workflow', icon: AlertTriangle },
                  { name: 'Payment Auth', endpoint: '/dev-debug/test-stored-payment-authorization', icon: CreditCard },
                ].map(test => (
                  <Card key={test.name} className="bg-slate-800 border-slate-700">
                    <CardContent className="p-4">
                      <Button 
                        onClick={() => runPreviewTest(test.name, test.endpoint)}
                        disabled={testLoading[test.name]}
                        variant="outline"
                        className="w-full border-slate-600 text-white hover:bg-slate-700"
                      >
                        {testLoading[test.name] ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <test.icon className="w-4 h-4 mr-2" />
                        )}
                        {test.name}
                      </Button>
                      {testResults[test.name] && (
                        <div className="mt-2">
                          <Badge className={testResults[test.name].summary?.all_passed || testResults[test.name].success
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                          }>
                            {testResults[test.name].summary?.all_passed || testResults[test.name].success ? 'PASSED' : 'FAILED'}
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

        </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white border-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => { confirmDialog.action?.(); setConfirmDialog(prev => ({ ...prev, open: false })); }}
              className={confirmDialog.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PlatformOwnerAppShell>
  );
};

export default PlatformCommandCentre;
